/**
 * The sandboxed preview-serve origin — where /preview/* (preview sessions)
 * and /s/* (share-serve sessions) user content is loaded from (#235).
 *
 * A dedicated subdomain on a different origin from both the frontend AND the
 * API, so:
 *   - the sandboxed iframe is cross-origin to the frontend
 *     (defense-in-depth on top of `sandbox=""`)
 *   - auth cookies set on the API host don't apply to the preview host, so
 *     iframe sub-requests can't piggyback on user auth
 *
 * Resolution order:
 *   1. Per-PR pages.dev hostname → preview-pr-<slug>-api.papan.ai. Checked
 *      BEFORE the baked value: per-PR review envs share a single CF Pages
 *      build with stagetest, so any baked VITE_PREVIEW_ORIGIN would be
 *      stagetest's (wrong backend — sessions live in pr-<slug>-api).
 *   2. VITE_PREVIEW_ORIGIN (build-time) — dev bakes http://localhost:3001;
 *      deployed envs bake their preview-<env> subdomain via the CF Pages
 *      project env vars.
 *   3. Runtime derivation from the hostname (@papan/api-client/origin) —
 *      covers deployed envs even with no baked value (#233 no-drift: the CF
 *      project env var becomes an override, not a requirement).
 *   4. Dev-LAN rehoming (#212 companion): a baked localhost origin is
 *      re-homed onto the hostname the page was actually loaded from, so
 *      phone-on-subnet testing (http://192.168.x.x:7474) reaches the API.
 *   5. '' — same-origin (the vite dev proxy serves it; a deployment with a preview host
 *      serves /s/ only there — #150 — so a host reaching this needs VITE_PREVIEW_ORIGIN).
 */
import { derivePreviewOrigin } from '@papan/api-client'

// CF Pages publishes two hostname shapes for preview deployments:
//   - branch alias:  <branch-slug>.papan-4dj.pages.dev  (e.g. `feat-foo`)
//   - commit hash:   <8-char-hash>.papan-4dj.pages.dev  (e.g. `a1b2c3d4`)
// Only the branch alias has a corresponding per-PR backend
// (pr-<slug>-api.papan.ai is provisioned at deploy time off the slug).
// Require a hyphen in the label to skip the hash form — those are
// historical deployment URLs; for them we fall through to the baked
// VITE_PREVIEW_ORIGIN, which is `preview-stagetest.papan.ai`. That
// won't have the right session either, so the iframe will 404, but
// at least we don't manufacture a non-existent host like
// `preview-pr-a1b2c3d4-api.papan.ai` and produce a confusing DNS error.
export const PR_PREVIEW_HOSTNAME = /^([a-z0-9]+(?:-[a-z0-9]+)+)\.papan-4dj\.pages\.dev$/i

const PREVIEW_ORIGIN = (() => {
  // Only a string origin counts (an unset or boolean-typed env var is '').
  const envOrigin: unknown = typeof import.meta !== 'undefined' ? import.meta.env?.VITE_PREVIEW_ORIGIN : undefined
  const baked = typeof envOrigin === 'string' ? envOrigin : ''
  if (typeof window === 'undefined') return baked
  const prMatch = window.location.hostname.match(PR_PREVIEW_HOSTNAME)
  if (prMatch) return `https://preview-pr-${prMatch[1]}-api.papan.ai`
  const resolved = baked || derivePreviewOrigin(window.location.hostname)
  // Dev LAN (#212 companion): dev bakes `http://localhost:3001`, but when
  // the SPA is opened from another device on the subnet
  // (http://192.168.x.x:7474 — phone testing) "localhost" resolves to THAT
  // device, so the sandbox iframe spins forever. Re-home the baked
  // localhost origin onto the host the page was actually loaded from (same
  // API, reachable address). Dev-only by construction — production preview
  // origins are never localhost.
  try {
    const u = new URL(resolved)
    const here = window.location.hostname
    if ((u.hostname === 'localhost' || u.hostname === '127.0.0.1') && here !== 'localhost' && here !== '127.0.0.1') {
      // `pnpm dev:lan` (#538) serves the SPA over https on its LAN name, and the API's port isn't
      // reachable from the network there: previews go through the dev server's /preview and /s
      // proxies on their own name, `preview.<lan-name>` (apps/web/vite.config.ts) — a different
      // origin from the SPA, as the SDK's handshake requires. (`http://<lan-name>:3001` from an
      // https page is blocked mixed content, and a frame left pointing at it took the whole
      // browser down in Chrome 156.)
      if (window.location.protocol === 'https:') return `https://preview.${window.location.host}`
      u.hostname = here
      return u.origin
    }
  } catch { /* unset/malformed → keep as resolved */ }
  return resolved
})()

/** The preview-serve origin ('' = same-origin). Stable for the page's lifetime. */
export function getPreviewOrigin(): string {
  return PREVIEW_ORIGIN
}

/**
 * Absolute URL for a share-serve resource: /s/<sessionId>/<path> on the
 * preview origin (#235 — off the Pages Function; the sessionId in the URL is
 * the auth, no cookies involved). `path` must already be URL-encoded.
 */
export function shareServeUrl(sessionId: string, path: string, query?: string): string {
  return `${PREVIEW_ORIGIN}/s/${sessionId}/${path}${query ? `?${query}` : ''}`
}
